import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useAuth } from '../../contexts/AuthContext';
import { customerService } from '../../services/customerService';
import RoleBasedNavigation from '../../components/ui/RoleBasedNavigation';
import AuthenticationPrompt from '../../components/ui/AuthenticationPrompt';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import CartItem from './components/CartItem';
import CartSummary from './components/CartSummary';

const ShoppingCart = () => {
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processingItemId, setProcessingItemId] = useState(null);

  useEffect(() => {
    if (user && userProfile?.role === 'customer') {
      loadCartItems();
    } else {
      setLoading(false);
    }
  }, [user, userProfile]);

  const loadCartItems = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get customer profile
      const { data: customer, error: customerError } = await customerService?.getCustomerByUserId(user?.id);
      if (customerError) throw customerError;
      if (!customer) throw new Error('Customer profile not found');

      // Get cart items
      const { data, error: cartError } = await customerService?.getCartItems(customer?.id);
      if (cartError) throw cartError;

      setCartItems(data || []);
    } catch (err) {
      console.error('Error loading cart:', err);
      setError(err?.message || 'Failed to load cart items');
    } finally {
      setLoading(false);
    }
  };

  const handleQuantityChange = async (cartItemId, newQuantity) => {
    if (newQuantity < 1) return;

    try {
      setProcessingItemId(cartItemId);
      const { error } = await customerService?.updateCartItemQuantity(cartItemId, newQuantity);
      if (error) throw error;

      // Update local state
      setCartItems((prev) =>
        prev?.map((item) =>
          item?.id === cartItemId ? { ...item, quantity: newQuantity } : item
        )
      );
    } catch (err) {
      console.error('Error updating quantity:', err);
      alert(err?.message || 'Failed to update quantity');
    } finally {
      setProcessingItemId(null);
    }
  };

  const handleRemoveItem = async (cartItemId) => {
    if (!confirm('Remove this item from your cart?')) return;

    try {
      setProcessingItemId(cartItemId);
      const { error } = await customerService?.removeFromCart(cartItemId);
      if (error) throw error;

      // Update local state
      setCartItems((prev) => prev?.filter((item) => item?.id !== cartItemId));
    } catch (err) {
      console.error('Error removing item:', err);
      alert(err?.message || 'Failed to remove item');
    } finally {
      setProcessingItemId(null);
    }
  };

  const handleClearCart = async () => {
    if (!confirm('Clear all items from your cart?')) return;

    try {
      setLoading(true);
      const { data: customer, error: customerError } = await customerService?.getCustomerByUserId(user?.id);
      if (customerError) throw customerError;

      const { error } = await customerService?.clearCart(customer?.id);
      if (error) throw error;

      setCartItems([]);
    } catch (err) {
      console.error('Error clearing cart:', err);
      alert(err?.message || 'Failed to clear cart');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckout = () => {
    if (cartItems?.length === 0) return;
    // Navigate to order placement with cart items
    navigate('/order-placement', { state: { cartItems } });
  };

  const calculateSubtotal = () => {
    return cartItems?.reduce((total, item) => {
      const price = parseFloat(item?.menu_item?.price_per_plate || 0);
      return total + price * item?.quantity;
    }, 0);
  };

  const calculateTax = (subtotal) => {
    return subtotal * 0.135; // 13.5% tax
  };

  const calculateTotal = () => {
    const subtotal = calculateSubtotal();
    const tax = calculateTax(subtotal);
    return subtotal + tax;
  };

  // Group cart items by supplier
  const groupedBySupplier = cartItems?.reduce((acc, item) => {
    const supplierId = item?.menu_item?.supplier?.id;
    if (!acc?.[supplierId]) {
      acc[supplierId] = {
        supplier: item?.menu_item?.supplier,
        items: []
      };
    }
    acc?.[supplierId]?.items?.push(item);
    return acc;
  }, {});

  if (!user) {
    return (
      <>
        <Helmet>
          <title>Shopping Cart - HungryElph</title>
        </Helmet>
        <RoleBasedNavigation />
        <div className="min-h-screen bg-background pt-24 pb-12">
          <AuthenticationPrompt
            title="Sign in to view your cart"
            message="Please sign in to access your shopping cart and manage your items."
          />
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Shopping Cart - HungryElph</title>
      </Helmet>
      <RoleBasedNavigation />
      <div className="min-h-screen bg-background pt-24 pb-12">
        <div className="max-w-7xl mx-auto px-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-4xl font-heading font-bold text-foreground mb-2">
                Shopping Cart
              </h1>
              <p className="text-muted-foreground">
                {cartItems?.length} {cartItems?.length === 1 ? 'item' : 'items'} in your cart
              </p>
            </div>
            {cartItems?.length > 0 && (
              <Button
                variant="outline"
                onClick={handleClearCart}
                disabled={loading}
                className="flex items-center gap-2"
              >
                <Icon name="Trash2" size={18} />
                Clear Cart
              </Button>
            )}
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="text-center">
                <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-muted-foreground">Loading your cart...</p>
              </div>
            </div>
          ) : error ? (
            <div className="bg-destructive/10 border border-destructive/20 rounded-xl p-6 text-center">
              <Icon name="AlertCircle" size={48} color="var(--color-destructive)" className="mx-auto mb-4" />
              <p className="text-destructive font-medium mb-2">Failed to load cart</p>
              <p className="text-muted-foreground mb-4">{error}</p>
              <Button onClick={loadCartItems}>Try Again</Button>
            </div>
          ) : cartItems?.length === 0 ? (
            <div className="bg-card rounded-xl p-12 text-center">
              <Icon name="ShoppingCart" size={64} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
              <h2 className="text-2xl font-heading font-bold text-foreground mb-2">
                Your cart is empty
              </h2>
              <p className="text-muted-foreground mb-6">
                Start adding delicious meals to your cart
              </p>
              <Button onClick={() => navigate('/customer-dashboard')}>
                Browse Menu
              </Button>
            </div>
          ) : (
            <div className="grid lg:grid-cols-3 gap-8">
              {/* Cart Items */}
              <div className="lg:col-span-2 space-y-6">
                {Object.values(groupedBySupplier)?.map((group) => (
                  <div key={group?.supplier?.id} className="bg-card rounded-xl p-6">
                    {/* Supplier Header */}
                    <div className="flex items-center gap-4 pb-4 mb-4 border-b border-border">
                      <img
                        src={group?.supplier?.profile_image || 'https://via.placeholder.com/48'}
                        alt={group?.supplier?.profile_image_alt || `${group?.supplier?.business_name} logo`}
                        className="w-12 h-12 rounded-lg object-cover"
                      />
                      <div>
                        <h3 className="font-heading font-bold text-foreground">
                          {group?.supplier?.business_name}
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          {group?.items?.length} {group?.items?.length === 1 ? 'item' : 'items'}
                        </p>
                      </div>
                    </div>

                    {/* Cart Items */}
                    <div className="space-y-4">
                      {group?.items?.map((item) => (
                        <CartItem
                          key={item?.id}
                          item={item}
                          onQuantityChange={handleQuantityChange}
                          onRemove={handleRemoveItem}
                          isProcessing={processingItemId === item?.id}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* Cart Summary */}
              <div className="lg:col-span-1">
                <CartSummary
                  subtotal={calculateSubtotal()}
                  tax={calculateTax(calculateSubtotal())}
                  total={calculateTotal()}
                  itemCount={cartItems?.length}
                  onCheckout={handleCheckout}
                  disabled={loading || cartItems?.length === 0}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ShoppingCart;